import { server_address } from '../api/Config';

// The one place that turns a stored imageUrl (user avatar, group icon) into
// a URL. Two kinds exist (see Backend/services/media/mediaPipeline.js):
//
//   media/<key>/s | media/<key>/a   processed WebP variants; `a` = animated
//   anything else                   a pre-pipeline upload, served as-is
//
// Returns null for "no image", so callers keep choosing their own fallback.
const MEDIA_REF = /^media\/([0-9a-f]{32})\/([as])$/;

export function parseImageRef(imageUrl) {
  if (!imageUrl) return null;
  const m = MEDIA_REF.exec(imageUrl);
  if (m) return { kind: 'media', key: m[1], animated: m[2] === 'a' };
  return { kind: 'legacy', file: imageUrl };
}

export function isAnimatedImage(imageUrl) {
  const ref = parseImageRef(imageUrl);
  return !!(ref && ref.kind === 'media' && ref.animated);
}

// size: the CSS size it's shown at. 128px covers anything up to 64px on a
// 2x screen; larger displays get the 512px variant.
// animated: play the animation, if the image has one. Off by default - lists
// show the first frame; the animation is for where someone is looking at it.
export function imageSrc(imageUrl, { size = 64, animated = false } = {}) {
  const ref = parseImageRef(imageUrl);
  if (!ref) return null;
  if (ref.kind === 'legacy') return `${server_address}api/users/avatars/${encodeURIComponent(ref.file)}`;
  const variant = size > 64 ? '512' : '128';
  return `${server_address}api/users/media/${ref.key}/${variant}${animated && ref.animated ? 'a' : ''}.webp`;
}

// Banners (5:2) have their own variant sizes: 600x240 for the card, 1200x480
// for the full profile.
export function bannerSrc(ref, { wide = false, animated = false } = {}) {
  const parsed = parseImageRef(ref);
  if (!parsed || parsed.kind !== 'media') return null;
  return `${server_address}api/users/media/${parsed.key}/${wide ? '1200' : '600'}${animated && parsed.animated ? 'a' : ''}.webp`;
}

// Profile colours: the text on top of them has to stay readable whatever
// two colours someone picked - light text on a dark pair, dark on a light.
function luminance(hex) {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function isLightPair(colors) {
  return (luminance(colors.primary) + luminance(colors.accent)) / 2 > 0.4;
}
